<script setup>
import { ref, computed, onMounted, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useDataTransfer } from 'dashboard/composables/useDataTransfer';
import { debounce } from '@chatwoot/utils';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useCompanyLabelsStore } from 'dashboard/stores/companyLabels';
import { DuplicateCompanyException } from 'shared/helpers/CustomErrors';

import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesCard from 'dashboard/components-next/Companies/CompaniesCard/CompaniesCard.vue';
import CompanyCreateDialog from 'dashboard/components-next/Companies/CompanyCreateDialog.vue';
import CompanyEmptyState from 'dashboard/components-next/Companies/EmptyState/CompanyEmptyState.vue';
import DataTransferImportDialog from 'dashboard/components-next/DataTransfer/DataTransferImportDialog.vue';
import CompanyExportDialog from 'dashboard/components-next/Companies/CompaniesForm/CompanyExportDialog.vue';
import CompanyImportDialog from 'dashboard/components-next/Companies/CompaniesForm/CompanyImportDialog.vue';
import ManageCompanyLabelsDialog from 'dashboard/components-next/Companies/CompaniesForm/ManageCompanyLabelsDialog.vue';
import CompaniesFilterDialog from 'dashboard/components-next/Companies/CompaniesFilterDialog.vue';
import CompaniesBulkActionBar from '../components/CompaniesBulkActionBar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';

const DEFAULT_SORT_FIELD = 'name';
const DEBOUNCE_DELAY = 300;

const companiesStore = useCompaniesStore();
const companyLabelsStore = useCompanyLabelsStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const allCompanies = computed(() => companiesStore.getCompaniesList);
const meta = computed(() => companiesStore.getMeta);

const activeFilters = ref({ countryCode: null, city: '' });
const filterDialogRef = ref(null);

const hasActiveFilters = computed(
  () => !!activeFilters.value.countryCode || !!activeFilters.value.city
);

const companies = computed(() => {
  const { countryCode, city } = activeFilters.value;
  if (!countryCode && !city) return allCompanies.value;
  return allCompanies.value.filter(c => {
    const matchCountry = !countryCode || c.countryCode === countryCode;
    const matchCity =
      !city || (c.city || '').toLowerCase().includes(city.toLowerCase());
    return matchCountry && matchCity;
  });
});
const uiFlags = computed(() => companiesStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const createCompanyDialogRef = ref(null);
const companyExportDialogRef = ref(null);
const companyImportDialogRef = ref(null);
const dataTransferDialogRef = ref(null);
const { isUnified, exportAll } = useDataTransfer();
const manageLabelsDialogRef = ref(null);
const bulkDeleteDialogRef = ref(null);
const pageNumber = computed(() => Number(route.query?.page) || 1);

// --- Bulk selection state ---
const selectedCompanyIds = ref([]);
const isSelecting = ref(false);
const isBulkActionLoading = ref(false);
const selectedCount = computed(() => selectedCompanyIds.value.length);
const hasSelection = computed(
  () => isSelecting.value || selectedCount.value > 0
);

const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);

const visibleCompanyIds = computed(() => companies.value.map(c => c.id));

const clearSelection = () => {
  selectedCompanyIds.value = [];
  isSelecting.value = false;
};

const startSelection = () => {
  isSelecting.value = true;
};

const openBulkDeleteDialog = () => {
  if (!selectedCompanyIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

const toggleSelectAll = shouldSelect => {
  const currentSelection = new Set(selectedCompanyIds.value);
  if (shouldSelect) {
    visibleCompanyIds.value.forEach(id => currentSelection.add(id));
  } else {
    visibleCompanyIds.value.forEach(id => currentSelection.delete(id));
  }
  selectedCompanyIds.value = Array.from(currentSelection);
};

const toggleCompanySelection = ({ id, value }) => {
  const isAlreadySelected = selectedCompanyIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedCompanyIds.value = [...selectedCompanyIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedCompanyIds.value = selectedCompanyIds.value.filter(
      companyId => companyId !== id
    );
  }
};

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
  }
});

// --- Sort / fetch ---
const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { companies_sort_by: companySortBy = DEFAULT_SORT_FIELD } =
  uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(companySortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeSort = computed(() => sortState.activeSort);
const activeOrdering = computed(() => sortState.activeOrdering);

const isFetchingList = computed(() => uiFlags.value.fetchingList);
const isCreatingCompany = computed(() => uiFlags.value.creatingItem);

const showEmptyStateLayout = computed(
  () =>
    !isFetchingList.value &&
    companies.value.length === 0 &&
    !searchValue.value &&
    !hasActiveFilters.value
);
const isUpdatingCompany = computed(() => uiFlags.value.updatingItem);
const isDeletingCompany = computed(() => uiFlags.value.deletingItem);

const goToCompanyDetail = companyId => {
  router.push({
    name: 'companies_dashboard_show',
    params: { accountId: route.params.accountId, companyId },
  });
};

const updateCompany = async ({ id, ...attrs }) => {
  try {
    await companiesStore.update({ id, ...attrs });
    useAlert(t('COMPANIES.DETAIL.PROFILE.MESSAGES.UPDATE_SUCCESS'));
  } catch (error) {
    if (
      error instanceof DuplicateCompanyException &&
      error.data.includes('cnpj')
    ) {
      useAlert(t('COMPANIES.DETAIL.PROFILE.MESSAGES.CNPJ_DUPLICATE'));
    } else {
      useAlert(t('COMPANIES.DETAIL.PROFILE.MESSAGES.UPDATE_ERROR'));
    }
  }
};

const deleteCompany = async id => {
  try {
    await companiesStore.delete(id);
    useAlert(t('COMPANIES.DETAIL.DELETE.MESSAGES.SUCCESS'));
  } catch {
    useAlert(t('COMPANIES.DETAIL.DELETE.MESSAGES.ERROR'));
  }
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const sortParam = computed(() => buildSortAttr());

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;

  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  if (currentSearch) {
    await companiesStore.search({
      search: currentSearch,
      page: currentPage,
      sort: currentSort,
    });
  } else {
    await companiesStore.get({
      page: currentPage,
      sort: currentSort,
    });
  }
};

// --- Bulk actions ---
const assignLabels = async labels => {
  if (!labels.length || !selectedCompanyIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Company',
      ids: selectedCompanyIds.value,
      labels: { add: labels },
    });
    useAlert(t('COMPANIES_BULK_ACTIONS.ASSIGN_LABELS_SUCCESS'));
    clearSelection();
    await fetchCompanies();
  } catch {
    useAlert(t('COMPANIES_BULK_ACTIONS.ASSIGN_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const removeLabels = async labels => {
  if (!labels.length || !selectedCompanyIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Company',
      ids: selectedCompanyIds.value,
      labels: { remove: labels },
    });
    useAlert(t('COMPANIES_BULK_ACTIONS.REMOVE_LABELS_SUCCESS'));
    clearSelection();
    await fetchCompanies();
  } catch {
    useAlert(t('COMPANIES_BULK_ACTIONS.REMOVE_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const deleteCompanies = async () => {
  if (!selectedCompanyIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Company',
      ids: selectedCompanyIds.value,
      action_name: 'delete',
    });
    useAlert(t('COMPANIES_BULK_ACTIONS.DELETE_SUCCESS'));
    clearSelection();
    await fetchCompanies();
    bulkDeleteDialogRef.value?.close?.();
  } catch {
    useAlert(t('COMPANIES_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const exportCompanies = async () => {
  try {
    await companiesStore.export();
    useAlert(t('COMPANIES_BULK_ACTIONS.EXPORT_SUCCESS'));
  } catch {
    useAlert(t('COMPANIES_BULK_ACTIONS.EXPORT_FAILED'));
  }
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  fetchCompanies(page, searchValue.value, sortParam.value);
};

const openCreateCompanyDialog = () => {
  createCompanyDialogRef.value?.dialogRef.open();
};

const openFilterDialog = () => filterDialogRef.value?.open();

const applyFilters = filters => {
  activeFilters.value = { ...filters };
};

const openExportDialog = () =>
  isUnified.value
    ? exportAll()
    : companyExportDialogRef.value?.dialogRef.open();
const openImportDialog = () =>
  isUnified.value
    ? dataTransferDialogRef.value?.open()
    : companyImportDialogRef.value?.dialogRef.open();
const openManageLabelsDialog = () =>
  manageLabelsDialogRef.value?.dialogRef.open();

const navigateToManageFields = () => {
  router.push({
    name: 'attributes_list',
    params: { accountId: route.params.accountId },
    query: { tab: 'company_attribute' },
  });
};

const companiesListLayoutRef = ref(null);

const onImport = async ({ file, fieldMapping }) => {
  try {
    await companiesStore.import({ file, fieldMapping });
    companyImportDialogRef.value?.onSuccess();
    companiesListLayoutRef.value?.startImportPolling();
    useAlert(t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.ERROR_MESSAGE'));
  }
};

const onExport = async () => {
  try {
    await companiesStore.export();
    useAlert(t('COMPANIES_LAYOUT.HEADER.ACTIONS.EXPORT.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('COMPANIES_LAYOUT.HEADER.ACTIONS.EXPORT.ERROR_MESSAGE'));
  }
};

const createCompany = async company => {
  const { contactId, labels, ...companyParams } = company;
  try {
    const newCompany = await companiesStore.create(companyParams);
    createCompanyDialogRef.value?.onSuccess();
    useAlert(t('COMPANIES.CREATE.MESSAGES.SUCCESS'));
    if (contactId) {
      try {
        await companiesStore.attachContactToCompany(newCompany.id, contactId);
      } catch {
        useAlert(t('COMPANIES.CREATE.MESSAGES.CONTACT_LINK_ERROR'));
      }
    }
    if (labels?.length) {
      try {
        await companyLabelsStore.updateCompanyLabels(newCompany.id, labels);
      } catch {
        // silent, mirrors the pattern already used for label persistence errors
      }
    }
    goToCompanyDetail(newCompany.id);
  } catch (error) {
    if (
      error instanceof DuplicateCompanyException &&
      error.data.includes('cnpj')
    ) {
      useAlert(t('COMPANIES.CREATE.MESSAGES.CNPJ_DUPLICATE'));
    } else {
      useAlert(t('COMPANIES.CREATE.MESSAGES.ERROR'));
    }
  }
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    companies_sort_by: buildSortAttr(),
  });

  fetchCompanies(1, searchValue.value, buildSortAttr());
};

onMounted(async () => {
  searchValue.value = searchQuery.value;

  if (!route.query.sort && sortParam.value !== DEFAULT_SORT_FIELD) {
    updateURLParams(pageNumber.value, searchQuery.value, sortParam.value);
  }

  await fetchCompanies();
});
</script>

<template>
  <CompaniesListLayout
    ref="companiesListLayoutRef"
    :search-value="searchValue"
    :header-title="t('COMPANIES.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :is-fetching-list="isFetchingList"
    :show-pagination-footer="!!companies.length && !hasSelection"
    :has-active-filters="hasActiveFilters"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @search="onSearch"
    @create="openCreateCompanyDialog"
    @import="openImportDialog"
    @export="openExportDialog"
    @manage-labels="openManageLabelsDialog"
    @manage-fields="navigateToManageFields"
    @start-selection="startSelection"
    @filter="openFilterDialog"
  >
    <CompanyEmptyState
      v-if="showEmptyStateLayout"
      :title="t('COMPANIES.EMPTY_STATE.TITLE')"
      :subtitle="t('COMPANIES.EMPTY_STATE.SUBTITLE')"
      :button-label="t('COMPANIES.EMPTY_STATE.BUTTON_LABEL')"
      @create="createCompany"
    />
    <div
      v-else-if="isFetchingList"
      class="flex items-center justify-center p-8"
    >
      <span class="text-n-slate-11 text-base">{{
        t('COMPANIES.LOADING')
      }}</span>
    </div>
    <template v-else>
      <CompaniesBulkActionBar
        v-if="hasSelection"
        :visible-company-ids="visibleCompanyIds"
        :selected-company-ids="selectedCompanyIds"
        :is-loading="isBulkActionLoading"
        @toggle-all="toggleSelectAll"
        @cancel-selection="clearSelection"
        @assign-labels="assignLabels"
        @remove-labels="removeLabels"
        @export-selected="exportCompanies"
        @delete-selected="openBulkDeleteDialog"
      />
      <div
        v-if="companies.length === 0"
        class="flex items-center justify-center p-8"
      >
        <span class="text-n-slate-11 text-base">{{
          t('COMPANIES.EMPTY_STATE.TITLE')
        }}</span>
      </div>
      <div
        v-else
        class="flex flex-col gap-4"
        :class="hasSelection ? 'pt-2' : 'pt-4'"
      >
        <CompaniesCard
          v-for="company in companies"
          :id="company.id"
          :key="company.id"
          :name="company.name"
          :domain="company.domain"
          :description="company.description"
          :email="company.email || ''"
          :phone-number="company.phoneNumber || ''"
          :city="company.city || ''"
          :country-code="company.countryCode || ''"
          :social-profiles="company.socialProfiles || {}"
          :cnpj="company.cnpj || ''"
          :custom-attributes="company.customAttributes || {}"
          :contacts-count="company.contactsCount || 0"
          :first-contacts="company.firstContacts || []"
          :avatar-url="company.avatarUrl"
          :created-at="company.createdAt"
          :last-activity-at="company.lastActivityAt"
          :is-updating="isUpdatingCompany"
          :is-deleting="isDeletingCompany"
          :selectable="hasSelection"
          :is-selected="selectedCompanyIds.includes(company.id)"
          disable-expand
          @name-click="goToCompanyDetail(company.id)"
          @update-company="updateCompany"
          @delete-company="deleteCompany"
          @select="value => toggleCompanySelection({ id: company.id, value })"
        />
        <Dialog
          v-if="selectedCount"
          ref="bulkDeleteDialogRef"
          type="alert"
          :title="bulkDeleteDialogTitle"
          :description="bulkDeleteDialogDescription"
          :confirm-button-label="bulkDeleteDialogConfirmLabel"
          :is-loading="isBulkActionLoading"
          @confirm="deleteCompanies"
        />
      </div>
    </template>
    <CompanyCreateDialog
      ref="createCompanyDialogRef"
      :is-loading="isCreatingCompany"
      @create="createCompany"
    />
    <CompanyExportDialog ref="companyExportDialogRef" @export="onExport" />
    <CompanyImportDialog ref="companyImportDialogRef" @import="onImport" />
    <DataTransferImportDialog
      ref="dataTransferDialogRef"
      @done="fetchCompanies()"
    />
    <ManageCompanyLabelsDialog ref="manageLabelsDialogRef" />
    <CompaniesFilterDialog
      ref="filterDialogRef"
      :active-filters="activeFilters"
      @apply="applyFilters"
    />
  </CompaniesListLayout>
</template>
